<template>
	<auth-page>
		<div class="login">
			<div class="image-wrapper">
				<img :src="application_logo_url" alt="Klantlogo">
			</div>
			<feedback-banner v-if="feedback" :purpose="PurposeOptions.danger" class="feedback-banner-overwrite">{{feedback}}</feedback-banner>
			<div class="form">
				<text-input v-model="username" :disabled="isLoading" :error="attempts > 0 && !username" :force-focus="focusUsernameInput" :size="InputSizeOptions.L" class="text-input-with-icon" placeholder="Email of inlognaam" @keydown.prevent.tab="usernameFocusNextField" @keydown.enter="usernameFocusNextField">
					<span :class="{'icon-holder': true, 'disabled': isLoading}"><user-icon class="icon"/></span>
				</text-input>
				<text-input v-if="showPasswordField" v-model="password" :disabled="isLoading" :error="attempts > 0 && !password" :force-focus="focusPasswordInput" :size="InputSizeOptions.L" class="text-input-with-icon" password placeholder="Wachtwoord" @keydown.enter="attemptLogin">
					<span :class="{'icon-holder': true, 'disabled': isLoading}"><key-round-icon class="icon"/></span>
				</text-input>
			</div>
			<simple-button v-if="showPasswordField" :disabled="isLoading" :size="ButtonSizeOptions.L" class="full-width-button" @click="attemptLogin">Inloggen</simple-button>
			<simple-button v-else :disabled="isLoading" :size="ButtonSizeOptions.L" class="full-width-button" @click="requestNewPassword">Nieuw wachtwoord instellen</simple-button>
			<simple-button v-if="showPasswordField" :disabled="isLoading" :emphasis="EmphasisOptions.medium" :size="ButtonSizeOptions.L" class="full-width-button" @click="showPasswordReset">Nieuw wachtwoord instellen</simple-button>
			<simple-button v-else :disabled="isLoading" :emphasis="EmphasisOptions.medium" :size="ButtonSizeOptions.L" class="full-width-button" @click="showLogin">Annuleren</simple-button>
			<div class="module">
				<table>
					<tbody>
						<tr>
							<td>
								<table>
									<tr v-for="module in packageVersies">
										<td>{{module.naam}}</td>
										<td>&nbsp;</td>
										<td>{{module.huidige_versie}}</td>
									</tr>
								</table>
							</td>
							<td>&nbsp;-&nbsp;</td>
							<td>
								<a href="http://datapas.nl/" rel="noopener" target="_blank">Datapas</a>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</auth-page>
	<tweede-factor-totp-modal :feedback="feedback" v-if="credentials_passed && !mfa_passed" :totp="totp" @close="showLogin" @login="attemptLogin" @updateTotp="updateTotp"/>
	<request-password-change-modal v-if="showRequestPasswordModal" :user-name="username" @close="closeRequestPasswordModal"/>
	<wait-animation v-if="isLoading" :context="ContextOptions.pagemask"/>
</template>
<script>
import {User as UserIcon, KeyRound as KeyRoundIcon} from "lucide-vue-next";

import {ContextOptions, FeedbackBanner, PurposeOptions, WaitAnimation} from "@datapas/vue-feedback";
import {EmphasisOptions, SimpleButton, SizeOptions as ButtonSizeOptions} from "@datapas/vue-button";
import {SizeOptions as InputSizeOptions, TextInput} from "@datapas/vue-input";

import AuthPage from "./components/AuthPage.vue";
import RequestPasswordChangeModal from "./components/RequestPasswordChangeModal.vue";
import TweedeFactorTotpModal from "./components/TweedeFactorTotpModal.vue";
import attemptLoginQuery from "./graphql/mutations/attemptLogin.graphql";
import requestPasswordChangeToken from "./graphql/mutations/requestPasswordChangeToken.graphql";
import {SmallModal} from "@datapas/vue-modal";

import packageVersiesQuery from "./graphql/unauthenticated-preload/packageVersies.graphql";

export default {
	name: "LoginPage",
	components: {
		TweedeFactorTotpModal,
		UserIcon,
		KeyRoundIcon,
		AuthPage,
		RequestPasswordChangeModal,
		SmallModal,
		FeedbackBanner,
		SimpleButton,
		TextInput,
		WaitAnimation
	},
	data() {
		return {
			ButtonSizeOptions,
			ContextOptions,
			EmphasisOptions,
			InputSizeOptions,
			PurposeOptions,
			application_logo_url: __DATAPAS_APPLICATION_LOGO__,
			packageVersies: {},
			showPasswordField: true,
			focusUsernameInput: 0,
			focusPasswordInput: 0,
			feedback: "",
			username: "",
			password: "",
			totp: "",
			credentials_passed: false,
			mfa_passed: false,
			attempts: 0,
			isLoading: false,
			showRequestPasswordModal: null
		};
	},
	mounted() {
		this.setFocusUsernameInput();
		this.fetchPackageVersies();
	},
	methods: {
		showPasswordReset() {
			this.feedback = "";
			this.showPasswordField = false;
			this.mfa_passed = false;
		},
		showLogin() {
			this.totp = "";
			this.feedback = "";
			this.showPasswordField = true;
			this.credentials_passed = false;
			this.mfa_passed = false;
		},
		requestNewPassword() {
			this.feedback = "";

			if (!this.validPasswordResetInput()) {
				return;
			}
			this.isLoading = true;
			this.$graphql.query(requestPasswordChangeToken, {
				login: this.username
			}).then(() => {
				this.showRequestPasswordModal = true;
			}).catch(result => {
				this.feedback = this.$graphql.getFeedbackFromErrors(result.errors).join("\n") || "Helaas ging er iets mis tijdens het instellen van een nieuw wachtwoord.\nProbeer het nogmaals of neem contact op met Datapas.";
			}).finally(() => {
				this.isLoading = false;
			});
		},
		closeRequestPasswordModal() {
			this.showRequestPasswordModal = false;
		},
		updateTotp(code) {
			this.totp = code;
		},
		attemptLogin() {
			this.feedback = "";

			if (!this.validLoginInput()) {
				return;
			}

			this.isLoading = true;
			this.$graphql.query(attemptLoginQuery, {
				username: this.username,
				password: this.password,
				totp: this.totp
			}).then(result => {
				const data = result.data.loginAttempt;
				if (data.credentials_passed && data.mfa_passed) {
					window.location.reload();
					return;
				}

				this.credentials_passed = data.credentials_passed;
				this.mfa_passed = data.mfa_passed;

				if (!data.credentials_passed) {
					this.attempts++;
					if (this.attempts > 2) {
						this.attempts = 0;
						this.feedback = 'Indien u uw wachtwoord vergeten bent kunt u een nieuw wachtwoord instellen door op de knop "Nieuw wachtwoord instellen" te klikken.';
					}
				}

				this.isLoading = false;
			}).catch(result => {
				this.feedback = this.$graphql.getFeedbackFromErrors(result.errors).join("\n") || "Helaas ging er iets mis tijdens het inloggen.\nProbeer het nogmaals of neem contact op met Datapas.";
				this.isLoading = false;
			});
		},
		validLoginInput() {
			let feedback = [];
			if (!this.username) {
				feedback.push("Inlognaam is verplicht.");
			}
			if (!this.password) {
				feedback.push("Wachtwoord is verplicht.");
			}

			if (feedback.length) {
				this.feedback = feedback.join("\n");
				return false;
			} else {
				return true;
			}
		},
		validPasswordResetInput() {
			if (!this.username) {
				this.feedback = "Inlognaam is verplicht.";
				return false;
			} else {
				return true;
			}
		},
		setFocusUsernameInput() {
			this.focusUsernameInput++;
		},
		usernameFocusNextField() {
			if (this.showPasswordField) {
				this.focusPasswordInput++;
			} else {
				this.requestNewPassword();
			}
		},
		fetchPackageVersies() {
			this.$graphql.query(packageVersiesQuery).then(result => {
				this.packageVersies = result.data.package_versies;
			}).catch(result => {
				this.feedback = this.$graphql.getFeedbackFromErrors(result.errors).join("\n") || "Helaas ging er iets mis tijdens het ophalen van de versies.\nProbeer het nogmaals of neem contact op met Datapas.";
			});
		}
	}
};
</script>
<style lang="scss" scoped>
@use "@datapas/scss-framework/src/vars";
@use "@datapas/scss-framework/src/form";
@use "scss/auth.scss";

$CONTENT-SPACING: calc(vars.$CONTENT-SPACING * 2);
$CONTENT-SPACING-GEGROEPEERD: calc($CONTENT-SPACING / 2);

.login {
	width: 350px;
	& > * {
		margin: 0 0 vars.$CONTENT-SPACING-GEGROEPEERD;

		&:first-child {
			margin: 0 0 $CONTENT-SPACING;
		}

		&:last-child {
			margin: 0;
		}

		&.form {
			margin-bottom: vars.$CONTENT-SPACING;
		}
	}

	& .image-wrapper {
		display: flex;
		justify-content: center;
		margin-bottom: $CONTENT-SPACING;
		& img {
			display: inline-block;
			max-width: 300px;
		}
	}

	& .text-input-with-icon {
		display: grid;
		grid-template-columns: vars.$INPUT-HEIGHT-LARGE 1fr;
		margin-top: vars.$CONTENT-SPACING-GEGROEPEERD;

		&:deep(.html-input) {
			border-bottom-left-radius: 0;
			border-top-left-radius: 0;
		}

		& .icon-holder {
			align-items: center;
			border-bottom: vars.$BORDER-WIDTH solid vars.$THEME-PRIMARY-COLOR;
			border-bottom-left-radius: vars.$BORDER-RADIUS-MEDIUM;
			border-left: vars.$BORDER-WIDTH solid vars.$THEME-PRIMARY-COLOR;
			border-top: vars.$BORDER-WIDTH solid vars.$THEME-PRIMARY-COLOR;
			border-top-left-radius: vars.$BORDER-RADIUS-MEDIUM;
			color: vars.$DISABLED-COLOR;
			display: flex;
			font-size: calc(vars.$INPUT-HEIGHT-LARGE / 2);
			justify-content: center;
			width: vars.$INPUT-HEIGHT-LARGE;

			&.disabled {
				border-color: vars.$DISABLED-COLOR;
			}

			& .icon {
				height: 26px;
				width: auto;
			}
		}
	}

	& .module {
		font-size: auth.$AUTH_TEXT_FONT_SIZE;
		padding-top: $CONTENT-SPACING-GEGROEPEERD;
		text-align: center;

		& table {
			border: none;
			border-collapse: collapse;
			border-spacing: 0;
			margin: auto;
			text-align: right;
		}

		& a {
			color: inherit;

			&:hover {
				color: vars.$THEME-PRIMARY-COLOR;
			}
		}
	}
}

.feedback-banner-overwrite {
	margin-bottom: vars.$CONTENT-SPACING;
	white-space: pre-line;
}
</style>